/**
 * Step-script contract for topic animations.
 *
 * Design rule: a step declares what the data IS, never where it is drawn.
 * No coordinates appear anywhere in this file. Renderers own all geometry,
 * which is what lets the same script render on a 1440px canvas and a 390px
 * phone, and what lets scripts be authored (and reviewed in a PR) as small
 * semantic JSON rather than hand-computed SVG.
 */

/** Semantic colour roles. Renderers map these to the platform palette. */
export type Tone = 'accent' | 'info' | 'success' | 'warn' | 'muted';

/**
 * A named cursor sitting at an array index (`left`, `right`, `i`, `slow`).
 *
 * `index` may equal `values.length`, which draws the pointer just past the last
 * cell. A loop that has consumed every element genuinely ends with its cursor
 * off the end, and forcing authors to park it on the last cell would misreport
 * the termination condition being taught.
 */
export interface IndexPointer {
  name: string;
  index: number;
  tone?: Tone;
}

/** Per-cell emphasis: visited, matched, discarded, in-window. */
export interface CellMark {
  index: number;
  tone: Tone;
  /** Tiny label drawn inside/below the cell (e.g. a running sum). */
  label?: string;
}

export interface ArrayView {
  kind: 'array';
  values: Array<number | string>;
  /** Small heading above the view — needed when a step shows two views at once. */
  caption?: string;
  /** `cells` is the default box row; `bars` is a histogram (rain water, skyline). */
  mode?: 'cells' | 'bars';
  pointers?: IndexPointer[];
  marks?: CellMark[];
  /** Inclusive bracket drawn around a contiguous range (sliding window). */
  window?: { start: number; end: number; label?: string; tone?: Tone };
  /** Defaults to true; the `[0] [1] [2]` index strip under each cell. */
  showIndices?: boolean;
}

export interface GridView {
  kind: 'grid';
  values: Array<Array<number | string>>;
  caption?: string;
  marks?: Array<{ row: number; col: number; tone: Tone; label?: string }>;
  cursor?: { row: number; col: number; label?: string } | null;
}

export interface LinkedListView {
  kind: 'linkedlist';
  nodes: Array<{ id: string; value: number | string; next?: string | null }>;
  caption?: string;
  /**
   * `nodeId: null` renders the pointer at the ∅ terminator — "curr is null" is
   * half of what linked-list code is about, so it has to be drawable.
   */
  pointers?: Array<{ name: string; nodeId: string | null; tone?: Tone }>;
  marks?: Array<{ nodeId: string; tone: Tone; label?: string }>;
  /**
   * No longer needed: any `next` that does not land on the immediately following
   * node is drawn as an arc automatically, so a splice that bypasses a node
   * cannot be mistaken for an edge through it. Retained for compatibility.
   */
  cyclic?: boolean;
}

export interface TreeView {
  kind: 'tree';
  /** `children` is positional: index 0 = left, 1 = right. `null` keeps the slot. */
  nodes: Array<{ id: string; value: number | string; children?: Array<string | null> }>;
  rootId: string;
  caption?: string;
  marks?: Array<{ nodeId: string; tone: Tone; label?: string }>;
  cursor?: string | null;
}

export interface GraphView {
  kind: 'graph';
  nodes: Array<{ id: string; label?: string }>;
  edges: Array<{ from: string; to: string; weight?: number; directed?: boolean; tone?: Tone }>;
  caption?: string;
  marks?: Array<{ nodeId: string; tone: Tone; badge?: string }>;
  /** `circle` suits general graphs; `layered` suits DAGs and topological sort. */
  layout?: 'circle' | 'layered';
}

/** Stack, queue, deque and heap-as-array all share one renderer. */
export interface StackView {
  kind: 'stack';
  values: Array<number | string>;
  caption?: string;
  /** `vertical` reads as a stack (top at the top); `horizontal` as a queue. */
  orientation?: 'vertical' | 'horizontal';
  marks?: CellMark[];
  /** Label for the open end, e.g. "top" or "front". */
  endLabel?: string;
}

/**
 * A freeform labeled box for diagrams that aren't array/tree/graph-shaped
 * (e.g. "Load balancer", "Cache", "Queue worker") — a plain card with a
 * label and whatever key/value attributes the author wants shown, rather
 * than a shape tied to a specific data structure.
 */
export interface NoteView {
  kind: 'note';
  label: string;
  attributes?: Record<string, string>;
  caption?: string;
}

/** One box/circle/diamond on a `DiagramView` — freely positioned and freely labeled/attributed, unlike `TreeView`/`GraphView` nodes whose layout the renderer derives from structure. */
export interface DiagramNode {
  id: string;
  label: string;
  shape?: 'box' | 'circle' | 'diamond';
  tone?: Tone;
  attributes?: Record<string, string>;
  /**
   * Position within the diagram's OWN coordinate space, author-controlled —
   * the one exception to this file's "renderers own all geometry" rule
   * (see the other views' comments). A free-form diagram's whole point is
   * where the author put things (e.g. "this subnet sits inside this VPC");
   * unlike an array/tree/graph, there is no structural layout to derive.
   * This stays mobile-safe the same way every other renderer already is:
   * the whole diagram is drawn once into one SVG `viewBox` that scales as a
   * unit to any screen width, exactly like a vector image — nothing here
   * reflows per-device the way, say, `ArrayView`'s cells might.
   */
  x: number;
  y: number;
}

/** A connection between two `DiagramNode`s, referenced by id. */
export interface DiagramEdge {
  id: string;
  source: string;
  target: string;
  label?: string;
  directed?: boolean;
}

/**
 * A freeform node-link diagram — for content that's neither an
 * array/tree/graph nor a single labeled box (`NoteView`), but a whole
 * arrangement of connected elements the author lays out themselves (system
 * diagrams, cloud architecture, flowcharts, network topology, …).
 */
export interface DiagramView {
  kind: 'diagram';
  nodes: DiagramNode[];
  edges: DiagramEdge[];
  caption?: string;
}

export type View =
  | ArrayView
  | GridView
  | LinkedListView
  | TreeView
  | GraphView
  | StackView
  | NoteView
  | DiagramView;

export type ViewKind = View['kind'];

/** Scalar values shown in the live `state` panel. */
export type StateValue = string | number | boolean | null;

export interface Step {
  /** 1-based index into the variant's `pseudocode`. Omit to highlight nothing. */
  line?: number;
  /** One or two sentences, present tense, explaining this step only. */
  narration: string;
  state?: Record<string, StateValue>;
  /**
   * Views rendered together for this step. More than one is deliberate: the
   * heap lesson shows a binary tree AND its array form with the same cells lit,
   * which is the entire point being taught.
   */
  views: View[];
}

/**
 * One approach to the topic. A `problem` script normally carries two variants —
 * brute force then optimized — played as a single continuous timeline so the
 * student feels the improvement rather than being told about it.
 */
export interface Variant {
  /**
   * Short name for the approach — "Brute force", "Sliding window". Keep it to
   * a couple of words: it appears in the variant strip, the code panel title
   * and the complexity badge, none of which can hold a sentence.
   */
  label: string;
  /** One sentence on why this approach costs what it costs. */
  note?: string;
  /** Compact display form only — `O(n·k)`, not `O(n·k) — k additions per window`. */
  complexity_time?: string;
  complexity_space?: string;
  /** Displayed verbatim, one entry per line. Blank strings render as spacing. */
  pseudocode: string[];
  steps: Step[];
}

export interface StepScript {
  variants: Variant[];
}

export interface TopicVisualization {
  kind: 'concept' | 'problem';
  leetcode_id?: number;
  difficulty?: 'easy' | 'medium' | 'hard';
  /** Slug in our own problems table, so Practice stays inside KodPrep. */
  problem_slug?: string;
  schema_version: number;
  script: StepScript;
}

/** Flattened timeline entry: every step across every variant, in order. */
export interface TimelineEntry {
  step: Step;
  variantIndex: number;
  /** 1-based position within the owning variant, for the per-variant dot strip. */
  stepInVariant: number;
}

/** Flattens variants into the single global timeline the transport scrubs over. */
export function buildTimeline(script: StepScript): TimelineEntry[] {
  const out: TimelineEntry[] = [];
  script.variants.forEach((variant, variantIndex) => {
    variant.steps.forEach((step, i) => {
      out.push({ step, variantIndex, stepInVariant: i + 1 });
    });
  });
  return out;
}
